// hooks/useBulkImport.js
import { useState, useCallback } from "react";
import axios from "axios";
import { parseImportFile } from "../utils/importParser";
import { validateImportData } from "../utils/importValidator";
import { useAppContext } from "../providers/appProvider";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const useBulkImport = (config, onImportComplete) => {
  const { config: appConfig } = useAppContext();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  // helpers

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationResult(null);
    setImportResult(null);
    setError(null);
    setIsLoading(false);
    setSkipDuplicates(true);
  }, []);

  const openDrawer = useCallback(() => {
    resetState();
    setIsOpen(true);
  }, [resetState]);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    resetState();
  }, [resetState]);

  // Step 1 : Parse file

  const handleFileSelect = useCallback(async (file) => {
  setIsLoading(true);
  setError(null);
  setSelectedFile(null);      
  setParsedData(null);  
  setValidationResult(null);
  setImportResult(null);

  try {
    const result = await parseImportFile(file);

    // ── Header format check ──
    const expectedHeaders = config.templateColumns.map(col =>
      t ? t(col.label) : col.label
    );
    const fileHeaders = result.headers || [];

    const unrecognizedHeaders = fileHeaders.filter(
      h => h !== '_rowNumber' && !expectedHeaders.includes(h)
    );

    const requiredHeaders = config.templateColumns
      .filter(col => col.required)
      .map(col => t ? t(col.label) : col.label);

    const missingRequiredHeaders = requiredHeaders.filter(
      h => !fileHeaders.includes(h)
    );

    if (unrecognizedHeaders.length > 0 || missingRequiredHeaders.length > 0) {
      setError(t("bulkImport.invalidFormat", {
        columns: [
          ...unrecognizedHeaders.map(h => t("bulkImport.unknownColumn", { name: h })),
          ...missingRequiredHeaders.map(h => t("bulkImport.missingColumn", { name: h })),
        ].join(", ")
      }));
      return null;
    }

    setSelectedFile(file);
    setParsedData(result);
    return result;
  } catch (err) {
    setError(err.message);
    throw err;
  } finally {
    setIsLoading(false);
  }
}, [config, t]);

  // Step 2 : Validate

  const validateFile = useCallback(
    async (data) => {
      const rows = data ?? parsedData?.data;
       if (!rows || !Array.isArray(rows) || rows.length === 0) {
        setError("bulkImport.noDataToValidate");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let existingRecords = [];

        if (config.duplicateCheck?.enabled) {
          const customerId = localStorage.getItem("customerId");
          const locationId =
            appConfig?.location?.LocationId ||
            localStorage.getItem("LocationId");

          const response = await axios.get(config.api.fetchExisting, {
            params: { customerId, locationId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          });

          existingRecords = response.data || [];
        }

        const validation = validateImportData(rows, config, existingRecords, t);
        if (config.postValidate) {
          await config.postValidate(validation, {
            customerId: localStorage.getItem("customerId"),
            t,
          });
        }
        setValidationResult(validation);
        return validation;
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [parsedData, config, appConfig, t],
  );

  // Step 3 : Import 

  const executeImport = useCallback(async () => {
    if (!validationResult) return;

    setIsLoading(true);
    setError(null);

    try {
      const rowsToImport = skipDuplicates
        ? validationResult.validRows
        : [...validationResult.validRows, ...validationResult.duplicateRows];

      if (rowsToImport.length === 0) {
        setError("bulkImport.noValidRecords");
        return;
      }

      const locationId =
        appConfig?.location?.LocationId || localStorage.getItem("LocationId");
      const customerId = localStorage.getItem("customerId");
      const createdBy = localStorage.getItem("UserId");

      const importPayload = rowsToImport.map((row) => ({
        ...row.data,
        LocationId: locationId,
        CustomerId: customerId,
        CreatedBy: createdBy,
      }));

      const response = await axios.post(
        config.api.import,
        { [config.api.payloadKey || "relations"]: importPayload },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
            "Content-Type": "application/json",
          },
        },
      );

    const result = {
      success: response.data?.successCount ?? importPayload.length,
      failed: response.data?.failedCount ?? 0,
      details: response.data,
    };

    if (result.failed === 0) {
      toast.success(
        `${result.success} ${t("bulkImport.imported")}`,
        { position: "top-right", autoClose: 4000 }
      );
    } else {
      toast.error(
        `${result.success} ${t("bulkImport.imported")}, ${result.failed} ${t("bulkImport.failed")}`,
        { position: "top-right", autoClose: 5000 }
      );
    }

    if (onImportComplete) {
      onImportComplete(response.data);
    }

    closeDrawer();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  }, [validationResult, skipDuplicates, config, appConfig, onImportComplete, closeDrawer, t]);


  return {
    isOpen,
    selectedFile,
    parsedData,
    validationResult,
    importResult,
    isLoading,
    error,
    skipDuplicates,
    setError,
    setSkipDuplicates,
    openDrawer,
    closeDrawer,
    handleFileSelect,
    validateFile,
    executeImport,
    resetState,
  };
};

export default useBulkImport;
